import axios from 'axios';
import { tokenUtils } from '../utils/tokenUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5116/api';

// Función para obtener headers de autenticación
const getAuthHeaders = () => {
  const token = tokenUtils.get();
  if (!token) throw new Error('No se encontró token de autenticación');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Clase de servicio que puede usar el hook
class UserProfileService {
  constructor() {
    this.apiUrl = API_URL;
  }

  // Método que recibe usuarioConCedula como parámetro
  async updateUserProfile(usuarioConCedula, profileData) {
    try {
      const response = await axios.put(
        `${this.apiUrl}/User/${usuarioConCedula.id}`,
        profileData,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      throw error;
    }
  }

  // Método que usa la cédula del usuario
  async getUserByCedula(cedula) {
    try {
      const response = await axios.get(`${this.apiUrl}/User/cedula/${cedula}`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener usuario por cédula:', error);
      throw error;
    }
  }

  /** GET /User/:id — usado para completar nombre y cédula en solicitudes que solo traen userId */
  async getUserById(userId) {
    if (userId == null || userId === '') {
      throw new Error('userId requerido');
    }
    try {
      const response = await axios.get(`${this.apiUrl}/User/${userId}`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener usuario por id (${userId}):`, error);
      throw error;
    }
  }

  // Método para obtener cursos del usuario
  async getUserCourses(usuarioConCedula) {
    try {
      const response = await axios.get(
        `${this.apiUrl}/UserCourse/user/${usuarioConCedula.id}/courses`,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al obtener cursos del usuario:', error);
      throw error;
    }
  }

  // Método para actualizar configuración del usuario
  async updateUserSettings(usuarioConCedula, settings) {
    try {
      const response = await axios.put(
        `${this.apiUrl}/User/${usuarioConCedula.id}/settings`,
        { settingsJson: JSON.stringify(settings) },
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al actualizar configuración:', error);
      throw error;
    }
  }

  // Método para obtener progreso de cursos
  async getUserCourseProgress(usuarioConCedula, courseId) {
    try {
      const response = await axios.get(
        `${this.apiUrl}/UserCourse/course-progress/${usuarioConCedula.id}/${courseId}`,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al obtener progreso del curso:', error);
      throw error;
    }
  }
}

// Función helper que combina el hook con el servicio
export const useUserProfileService = () => {
  // Esta función debe ser llamada dentro de un componente React
  // que tenga acceso al hook useUsuarioConCedula
  return {
    updateProfile: async (profileData, usuarioConCedula) => {
      const service = new UserProfileService();
      return await service.updateUserProfile(usuarioConCedula, profileData);
    },
    
    getUserCourses: async (usuarioConCedula) => {
      const service = new UserProfileService();
      return await service.getUserCourses(usuarioConCedula);
    },
    
    updateSettings: async (settings, usuarioConCedula) => {
      const service = new UserProfileService();
      return await service.updateUserSettings(usuarioConCedula, settings);
    },
    
    getCourseProgress: async (courseId, usuarioConCedula) => {
      const service = new UserProfileService();
      return await service.getUserCourseProgress(usuarioConCedula, courseId);
    }
  };
};

export default UserProfileService; 